// BiometricLoginToggle — "Unlock with biometrics" setting for this device.
//
// Deliberately NOT a Cognito passkey sign-in factor: Cognito refuses WEB_AUTHN
// as a first factor for users who have TOTP MFA activated, and TOTP is mandatory
// here (SOC-2). Instead this enables a local biometric lock over the persisted
// session, so day-to-day the user opens the app and touches the sensor instead
// of switching to an authenticator app to copy a code.
import React, { useState } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import {
  isAppLockSupported,
  isAppLockEnabled,
  enableAppLock,
  disableAppLock,
} from '../../utils/appLock';
import { isMobileDevice } from '../../utils/device';
import FingerprintIcon from './FingerprintIcon';
import './BiometricLoginToggle.css';

function friendlyError(err) {
  const name = err?.name || 'Error';
  const msg = err?.message || '';
  const detail = `${name}: ${msg || 'unknown'}`;
  if (name === 'NotAllowedError' || /cancel|timed out|not allowed/i.test(msg)) {
    return 'Biometric prompt was cancelled. You can try again.';
  }
  if (name === 'NotSupportedError' || name === 'SecurityError' || name === 'AbortError') {
    return `This device couldn’t register a biometric. [${detail}]`;
  }
  return `Couldn’t enable biometric unlock. [${detail}]`;
}

const BiometricLoginToggle = () => {
  const { userEmail } = useFileContext();
  const offered = isAppLockSupported() && isMobileDevice();
  const [enabled, setEnabled] = useState(isAppLockEnabled());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleEnable = async () => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await enableAppLock(userEmail);
      setEnabled(true);
      setNotice('Biometric unlock is on for this device.');
    } catch (err) {
      console.warn('App lock enable failed:', err);
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = () => {
    const ok = window.confirm('Turn off biometric unlock on this device?');
    if (!ok) return;
    disableAppLock();
    setEnabled(false);
    setNotice('Biometric unlock is off for this device.');
    setError('');
  };

  return (
    <div className="biometric-toggle">
      <div className="biometric-toggle__head">
        <span className="biometric-toggle__icon" aria-hidden="true">
          <FingerprintIcon />
        </span>
        <div>
          <h3 className="biometric-toggle__title">Unlock with biometrics</h3>
          <p className="biometric-toggle__subtitle">
            Open the app and use your fingerprint or face instead of typing a code from your
            authenticator app every time.
          </p>
        </div>
      </div>

      {!offered ? (
        <p className="biometric-toggle__unsupported">
          Biometric unlock is available on your mobile device. On desktop, sign in with your
          password and authenticator code.
        </p>
      ) : enabled ? (
        <>
          <p className="biometric-toggle__status">Enabled on this device.</p>
          <button type="button" className="biometric-toggle__remove biometric-toggle__remove--block" onClick={handleDisable}>
            Turn off biometric unlock
          </button>
        </>
      ) : (
        <button type="button" className="biometric-toggle__enroll" onClick={handleEnable} disabled={busy}>
          {busy ? 'Waiting for biometric…' : 'Enable biometric unlock'}
        </button>
      )}

      {error && <p className="biometric-toggle__error">{error}</p>}
      {notice && <p className="biometric-toggle__notice">{notice}</p>}

      <p className="biometric-toggle__recovery">
        This protects the app on this device. You still sign in with your password and
        authenticator code — biometrics never replace them.
      </p>
    </div>
  );
};

export default BiometricLoginToggle;
